import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import type { MergedWorkspace } from './types';
import { useSampleWorkspace } from './sample-workspace';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useEnvironmentsOptional } from '@/services/db/environment/queries';
import { useObjectTypesOptional } from '@/services/db/object-type/queries';

/**
 * The account's workspaces, with the sample workspace last when the user can see
 * one. Reads `useEnvironmentsOptional` with the signed-in account and the sample
 * workspace filled in.
 */
export function useListWorkspaces<TSelected = MergedWorkspace[]>({
  enabled = true,
  meta,
  select,
}: {
  enabled?: boolean;
  meta?: { errorMessage: string };
  select?: (workspaces: MergedWorkspace[]) => TSelected;
} = {}) {
  const accountId = useStore(authStore).account?.id;
  const { sampleAccountId, sampleWorkspaceId } = useSampleWorkspace();
  const sampleWorkspace = useMemo(
    () =>
      sampleAccountId != null && sampleWorkspaceId != null
        ? { accountId: sampleAccountId, workspaceId: sampleWorkspaceId }
        : null,
    [sampleAccountId, sampleWorkspaceId],
  );
  return useEnvironmentsOptional({ accountId, sampleWorkspace, enabled, meta, select });
}

export const useWorkspaceCount = () => {
  return useListWorkspaces({
    select: (data) => data.length,
  });
};

interface UseFilterWorkspacesByNameOptions {
  includeSampleWorkspace?: boolean;
}
export const useFilterWorkspacesByName = (
  name: string,
  { includeSampleWorkspace = false }: UseFilterWorkspacesByNameOptions = {},
) => {
  const lowerCaseName = name.toLowerCase();
  const { isSampleWorkspace } = useSampleWorkspace();

  return useListWorkspaces({
    select: (data) => {
      const sampleWorkspace = data.find((w) => isSampleWorkspace(w.cio_workspace_id));

      const shouldIncludeSampleWorkspace = includeSampleWorkspace && sampleWorkspace != null;
      return (
        data
          .filter((w) => w.name.toLowerCase().includes(lowerCaseName) && !isSampleWorkspace(w.cio_workspace_id))
          .sort((a, b) => a.name.localeCompare(b.name))
          // Add demo workspace to the end of the list
          .concat(shouldIncludeSampleWorkspace ? [sampleWorkspace] : [])
      );
    },
  });
};

/** The workspace's object types. Builder-plan accounts have no custom objects, so the read stays off for them. */
export const useListWorkspaceObjectTypes = (workspaceId?: number) => {
  const builder = isBuilderPlan(useStore(authStore).account);
  return useObjectTypesOptional(workspaceId, { enabled: !builder });
};
